class CartProgressBar extends HTMLElement {
  constructor() {
    super();
    
    // Milestone thresholds - will be set from data attributes
    this.freeDeliveryThreshold = 40;
    this.freeGiftThreshold = 80;
    this.hasFreeGift = true; // Default to true, will be set from data attribute
    
    // Elements
    this.messageElement = null;
    this.progressFill = null;
    this.deliveryMilestone = null;
    this.giftMilestone = null;
    this.ghostProduct = null;
    
    // Bind methods
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.updateDisplay = this.updateDisplay.bind(this);
    this.fetchAndUpdate = this.fetchAndUpdate.bind(this);
  }

  connectedCallback() {
    // Get thresholds from data attributes
    if (this.dataset.deliveryThreshold) {
      this.freeDeliveryThreshold = parseFloat(this.dataset.deliveryThreshold);
    }
    if (this.dataset.giftThreshold) {
      this.freeGiftThreshold = parseFloat(this.dataset.giftThreshold);
    }
    
    // Check if free gift is configured
    if (this.dataset.hasFreeGift) {
      this.hasFreeGift = this.dataset.hasFreeGift === 'true';
    }
    
    // Get DOM elements
    this.messageElement = this.querySelector('.wt-progress-bar-message');
    this.progressFill = this.querySelector('.wt-progress-bar__fill');
    this.deliveryMilestone = this.querySelector('.wt-milestone--delivery');
    this.giftMilestone = this.querySelector('.wt-milestone--gift');
    
    // Find ghost product element in the cart drawer
    this.ghostProduct = document.querySelector('.wt-cart-ghost-product');
    
    // Subscribe to cart update events using the theme's pub/sub system
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
    
    // Initial update
    this.fetchAndUpdate();
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  handleCartUpdate(event) {
    // When cart is updated, fetch fresh cart data
    this.fetchAndUpdate();
  }

  async fetchAndUpdate() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateFromCart(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  updateFromCart(cart) {
    // Validate cart data
    if (!cart || typeof cart.total_price === 'undefined' || cart.total_price === null) {
      console.warn('Invalid cart data received:', cart);
      return;
    }
    
    // Get cart value in currency units (divide by 100 for cents/pence)
    const cartValue = cart.total_price / 100;
    
    // Extra validation to ensure we have a valid number
    if (isNaN(cartValue) || !isFinite(cartValue)) {
      console.warn('Invalid cart value calculated:', cartValue);
      return;
    }
    
    // Calculate remaining amounts
    const deliveryRemaining = Math.max(0, this.freeDeliveryThreshold - cartValue);
    const giftRemaining = Math.max(0, this.freeGiftThreshold - cartValue);
    
    // Determine milestone status
    const deliveryUnlocked = cartValue >= this.freeDeliveryThreshold;
    const giftUnlocked = this.hasFreeGift && cartValue >= this.freeGiftThreshold;
    
    // Calculate progress percentage based on whether free gift is enabled
    let progressPercentage;
    if (this.hasFreeGift) {
      // Progress based on gift threshold when gift is enabled
      progressPercentage = Math.min(100, (cartValue / this.freeGiftThreshold) * 100);
    } else {
      // Progress based on delivery threshold when no gift
      progressPercentage = Math.min(100, (cartValue / this.freeDeliveryThreshold) * 100);
    }
    
    // Get message templates from data attributes
    const deliveryMessage = this.dataset.deliveryMessage || 'Spend ||amount|| more for free delivery!';
    const giftMessage = this.dataset.giftMessage || 'Spend ||amount|| more for a free gift!';
    const successMessage = this.dataset.successMessage || 'Free Shipping & Free Gift Unlocked!';
    
    // Determine message based on whether free gift is enabled
    let message = '';
    if (this.hasFreeGift) {
      if (giftUnlocked) {
        message = successMessage;
      } else if (deliveryUnlocked) {
        const remainingAmount = this.formatMoney(giftRemaining * 100);
        message = giftMessage.replace('||amount||', remainingAmount);
      } else {
        const remainingAmount = this.formatMoney(deliveryRemaining * 100);
        message = deliveryMessage.replace('||amount||', remainingAmount);
      }
    } else {
      // Only delivery milestone when no gift configured
      if (deliveryUnlocked) {
        message = successMessage;
      } else {
        const remainingAmount = this.formatMoney(deliveryRemaining * 100);
        message = deliveryMessage.replace('||amount||', remainingAmount);
      }
    }
    
    // Update the display with animation
    this.updateDisplay(progressPercentage, message, deliveryUnlocked, giftUnlocked);
    
    // Update ghost product if it exists and gift is enabled
    if (this.hasFreeGift) {
      this.updateGhostProduct(giftUnlocked);
    }
  }

  updateDisplay(progressPercentage, message, deliveryUnlocked, giftUnlocked) {
    // Update message
    if (this.messageElement) {
      this.messageElement.textContent = message;
    }
    
    // Animate progress bar fill using requestAnimationFrame for smooth animation
    if (this.progressFill) {
      requestAnimationFrame(() => {
        this.progressFill.style.width = `${progressPercentage}%`;
      });
    }
    
    // Update delivery milestone state
    if (this.deliveryMilestone) {
      if (deliveryUnlocked) {
        this.deliveryMilestone.classList.add('wt-milestone--unlocked');
      } else {
        this.deliveryMilestone.classList.remove('wt-milestone--unlocked');
      }
    }
    
    // Update gift milestone state
    if (this.giftMilestone) {
      if (giftUnlocked) {
        this.giftMilestone.classList.add('wt-milestone--unlocked');
      } else {
        this.giftMilestone.classList.remove('wt-milestone--unlocked');
      }
    }
  }

  updateGhostProduct(giftUnlocked) {
    // Re-query ghost product in case DOM has updated
    this.ghostProduct = document.querySelector('.wt-cart-ghost-product');
    
    if (this.ghostProduct) {
      if (giftUnlocked) {
        this.ghostProduct.classList.add('wt-cart-ghost-product--unlocked');
        this.ghostProduct.classList.remove('wt-cart-ghost-product--locked');
      } else {
        this.ghostProduct.classList.add('wt-cart-ghost-product--locked');
        this.ghostProduct.classList.remove('wt-cart-ghost-product--unlocked');
      }
    }
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || window.Shopify?.money_format || '£{{amount}}');
    }
    
    // Fallback to simple formatting
    const amount = (cents / 100).toFixed(2);
    return window.theme?.currencySymbol ? `${window.theme.currencySymbol}${amount}` : `£${amount}`;
  }
}

customElements.define('cart-progress-bar', CartProgressBar);
